import type { GitHubConfig } from '../types';
import { buildLfsPointer, parseLfsPointerBytes, type LfsPointer } from '../utils/gitLfsPointer';

const LFS_MIME = 'application/vnd.git-lfs+json';

/** GitHub Contents API practical max for browser base64 upload (bytes). */
export const GITHUB_DIRECT_BLOB_MAX_BYTES = 95 * 1024 * 1024;

export async function sha256Oid(bytes: Uint8Array): Promise<string> {
  const copy = new Uint8Array(bytes);
  const hash = await crypto.subtle.digest('SHA-256', copy);
  const hex = Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  return `sha256:${hex}`;
}

type BatchObject = {
  oid: string;
  size: number;
  actions?: {
    upload?: { href: string; header?: Record<string, string>; expires_in?: number };
    download?: { href: string; header?: Record<string, string>; expires_in?: number };
    verify?: { href: string; header?: Record<string, string>; expires_in?: number };
  };
};

type BatchResponse = { objects?: BatchObject[] };

function useSameOriginLfsProxy(): boolean {
  return typeof window !== 'undefined' && typeof fetch === 'function';
}

function lfsAuthHeaders(config: GitHubConfig): Record<string, string> {
  return {
    Accept: LFS_MIME,
    'Content-Type': 'application/json',
    Authorization: `Bearer ${config.pat}`,
  };
}

export function isLikelyCorsOrNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) {
    const msg = err.message.toLowerCase();
    return msg.includes('network') || msg.includes('fetch') || msg.includes('cors');
  }
  return false;
}

async function lfsBatch(
  config: GitHubConfig,
  operation: 'upload' | 'download',
  objects: LfsPointer[]
): Promise<BatchObject[]> {
  const payload = {
    owner: config.owner,
    repo: config.repo,
    branch: config.branch.trim() || 'main',
    operation,
    objects: objects.map(o => ({ oid: o.oid, size: o.size })),
  };

  const res = useSameOriginLfsProxy()
    ? await fetch('/api/github-lfs/batch', {
        method: 'POST',
        headers: lfsAuthHeaders(config),
        cache: 'no-store',
        body: JSON.stringify(payload),
      })
    : await fetch(`https://github.com/${config.owner}/${config.repo}.git/info/lfs/objects/batch`, {
        method: 'POST',
        headers: {
          Accept: LFS_MIME,
          'Content-Type': LFS_MIME,
          Authorization: `Bearer ${config.pat}`,
        },
        cache: 'no-store',
        body: JSON.stringify({
          operation,
          transfers: ['basic'],
          ref: { name: `refs/heads/${payload.branch}` },
          objects: payload.objects,
        }),
      });

  const text = await res.text();
  if (!res.ok) {
    throw new Error(`Git LFS batch ${operation} ${res.status}: ${text}`);
  }
  const data = JSON.parse(text) as BatchResponse;
  return data.objects ?? [];
}

async function uploadViaActions(
  bytes: Uint8Array,
  oid: string,
  size: number,
  actions: NonNullable<BatchObject['actions']>
): Promise<void> {
  const upload = actions.upload;
  if (!upload?.href) return;

  const uploadHeaders: Record<string, string> = { ...(upload.header ?? {}) };
  const putRes = await fetch(upload.href, {
    method: 'PUT',
    headers: uploadHeaders,
    body: new Blob([Uint8Array.from(bytes)]),
    cache: 'no-store',
  });
  if (!putRes.ok) {
    throw new Error(`Git LFS upload ${putRes.status}: ${await putRes.text()}`);
  }

  const verify = actions.verify;
  if (verify?.href) {
    const verifyHeaders: Record<string, string> = {
      Accept: LFS_MIME,
      'Content-Type': LFS_MIME,
      ...(verify.header ?? {}),
    };
    const verRes = await fetch(verify.href, {
      method: 'POST',
      headers: verifyHeaders,
      cache: 'no-store',
      body: JSON.stringify({ oid, size }),
    });
    if (!verRes.ok) {
      throw new Error(`Git LFS verify ${verRes.status}: ${await verRes.text()}`);
    }
  }
}

/** Upload bytes to Git LFS storage (no-op if server already has the object). */
export async function uploadGitLfsObject(config: GitHubConfig, bytes: Uint8Array): Promise<LfsPointer> {
  const oid = await sha256Oid(bytes);
  const size = bytes.byteLength;
  const pointer = { oid, size };
  const results = await lfsBatch(config, 'upload', [pointer]);
  const entry = results[0];
  if (entry?.actions) {
    await uploadViaActions(bytes, oid, size, entry.actions);
  }
  return pointer;
}

/** Download bytes for a pointer from Git LFS storage. */
export async function downloadGitLfsObject(config: GitHubConfig, pointer: LfsPointer): Promise<Uint8Array> {
  const results = await lfsBatch(config, 'download', [pointer]);
  const download = results[0]?.actions?.download;
  if (!download?.href) {
    throw new Error(`Git LFS: no download action for ${pointer.oid}`);
  }
  const headers: Record<string, string> = { ...(download.header ?? {}) };
  const res = await fetch(download.href, { headers, cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Git LFS download ${res.status}: ${await res.text()}`);
  }
  return new Uint8Array(await res.arrayBuffer());
}

export function tryParseStoredLfsPointer(bytes: Uint8Array): LfsPointer | null {
  return parseLfsPointerBytes(bytes);
}

/** Expand Git LFS pointer file bytes to the stored media payload. */
export async function resolveCommittedMediaBytes(
  config: GitHubConfig,
  bytes: Uint8Array
): Promise<Uint8Array> {
  const ptr = tryParseStoredLfsPointer(bytes);
  if (!ptr) return bytes;
  return downloadGitLfsObject(config, ptr);
}

export function pointerFileContent(pointer: LfsPointer): string {
  return buildLfsPointer(pointer.oid, pointer.size);
}

export function encodeBytesAsBase64Content(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}
